/**
 * Generic availability helper functions that operate on AvailabilityBucket.
 * These functions are entity-agnostic and can be used with any entity that has an AvailabilityBucket.
 */

import type { Moment } from "moment"
import { isDateOnly, isMidnight, overlapsDate } from "@/helper/time-helper.ts"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import { AvailabilityBlock } from "@/models/availability-block.model.ts"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import type { RecurringAvailability } from "@/models/recurring-availability.model.ts"
import { WeekdayLabels } from "@/enums/models/weekdays.ts"

// ============================================================================
// TYPES
// ============================================================================

export interface UnavailabilityBlock {
  source: AvailabilityBlock | RecurringAvailability
  timeStart: Moment
  timeEnd: Moment
}

// ============================================================================
// CLASS DEFINITION
// ============================================================================

/**
 * Base availability helper class that operates on AvailabilityBucket.
 * This class is entity-agnostic and can be used with any entity that has an AvailabilityBucket.
 */
export class AvailabilityHelper {
  // ============================================================================
  // TIME / MOMENT HELPERS
  // ============================================================================

  /**
   * Converts a moment to minutes since midnight.
   */
  private static toMinutes(time: Moment): number {
    return time.hours() * 60 + time.minutes()
  }

  /**
   * Adjusts a moment for midnight handling.
   * For start bounds (isEnd=false): midnight becomes the start of that day.
   * For end bounds (isEnd=true): midnight becomes the start of the next day.
   * Non-midnight values are returned as-is.
   */
  protected static adjustForMidnight(
    time: Moment | null | undefined,
    isEnd: boolean
  ): Moment | null | undefined {
    if (!isMidnight(time)) {
      return time
    }

    return isEnd ? time?.clone().add(1, "day").startOf("day") : time?.clone().startOf("day")
  }

  /**
   * Checks whether a moment falls inside an availability block's time bounds.
   * Midnight bounds are treated as whole-day; date-only checks use day-level overlap.
   */
  static isMomentWithinAvailabilityBlock(
    timeStart: Moment | null | undefined,
    timeEnd: Moment | null | undefined,
    date: Moment
  ): boolean {
    const effectiveTimeStart = this.adjustForMidnight(timeStart, false)
    const effectiveTimeEnd = this.adjustForMidnight(timeEnd, true)

    if (isDateOnly(date)) {
      return overlapsDate(effectiveTimeStart, effectiveTimeEnd, date)
    }

    if (effectiveTimeStart && date.isBefore(effectiveTimeStart)) {
      return false
    }

    return !(effectiveTimeEnd && !date.isBefore(effectiveTimeEnd))
  }

  /**
   * Returns whether the given moment falls inside the recurring availability slot.
   * Recurring availability times are stored as time-only moments, so only the time
   * portion is relevant here.
   */
  static isTimeWithinRecurringAvailabilitySlot(
    recurringAvailability: RecurringAvailability,
    date: Moment
  ): boolean {
    const { timeStart, timeEnd } = recurringAvailability
    if (!timeStart && !timeEnd) {
      return true
    }

    const dateMinutes = this.toMinutes(date)
    const startMinutes = timeStart ? this.toMinutes(timeStart) : 0
    const endMinutes = timeEnd ? this.toMinutes(timeEnd) : Infinity

    return dateMinutes >= startMinutes && dateMinutes < endMinutes
  }

  // ============================================================================
  // BUCKET-LEVEL QUERIES
  // ============================================================================

  /**
   * Returns active recurring slots, optionally filtered by availability type.
   */
  private static getActiveRecurringSlots(
    bucket: AvailabilityBucket,
    availabilityType?: AvailabilityTypes
  ): RecurringAvailability[] {
    return bucket.recurringAvailabilities.filter(
      (r) =>
        r.isActive && (availabilityType === undefined || r.availabilityType === availabilityType)
    )
  }

  /**
   * Returns whether the bucket has recurring availability restrictions.
   * This includes both AVAILABLE slots (which restrict availability to those times)
   * and UNAVAILABLE slots (which explicitly mark times as unavailable).
   */
  static hasRecurringAvailabilityRestrictions(bucket: AvailabilityBucket): boolean {
    return this.getActiveRecurringSlots(bucket).length > 0
  }

  /**
   * Returns whether the bucket has active AVAILABLE recurring slots.
   * These slots restrict availability to specific times (inverse logic).
   */
  static hasAvailableRecurringSlots(bucket: AvailabilityBucket): boolean {
    return this.getActiveRecurringSlots(bucket, AvailabilityTypes.AVAILABLE).length > 0
  }

  /**
   * Returns active recurring availability slots that apply to the given calendar day,
   * optionally filtered by availability type.
   */
  static findRecurringAvailabilitiesForDate(
    bucket: AvailabilityBucket,
    date: Moment,
    availabilityType?: AvailabilityTypes
  ): RecurringAvailability[] {
    const isoWeekday = date.isoWeekday()
    const shouldCheckTime = !isDateOnly(date)

    return this.getActiveRecurringSlots(bucket, availabilityType).filter(
      (r) =>
        r.dayOfWeek === isoWeekday &&
        (!shouldCheckTime || this.isTimeWithinRecurringAvailabilitySlot(r, date))
    )
  }

  /**
   * Returns whether the bucket allows recurring availability on the given calendar day.
   * Only AVAILABLE slots count as positive availability. UNAVAILABLE slots do not grant access.
   * If there are no active AVAILABLE recurring slots at all, availability is assumed (no restrictions).
   */
  static isWithinRecurringAvailability(bucket: AvailabilityBucket, date: Moment): boolean {
    if (!this.hasAvailableRecurringSlots(bucket)) {
      return true
    }

    return (
      this.findRecurringAvailabilitiesForDate(bucket, date, AvailabilityTypes.AVAILABLE).length > 0
    )
  }

  /**
   * Returns UNAVAILABLE availability blocks that have at least one
   * time bound (timeStart or timeEnd), else they are just useless.
   * Excludes base availability blocks (AVAILABLE) and blocks with no time range.
   */
  private static getTimeBoundedUnavailableBlocks(bucket: AvailabilityBucket): AvailabilityBlock[] {
    return bucket.availabilityBlocks.filter(
      (block) =>
        block.availabilityType === AvailabilityTypes.UNAVAILABLE &&
        (!!block.timeStart || !!block.timeEnd)
    )
  }

  /**
   * Returns availability blocks with type UNAVAILABLE that overlap the given calendar day.
   */
  static findUnavailableBlocksForDate(
    bucket: AvailabilityBucket,
    date: Moment
  ): AvailabilityBlock[] {
    return this.getTimeBoundedUnavailableBlocks(bucket).filter((block) =>
      AvailabilityHelper.isMomentWithinAvailabilityBlock(block.timeStart, block.timeEnd, date)
    )
  }

  // ============================================================================
  // FORMATTING
  // ============================================================================

  /**
   * Formats a time range string: "vanaf HH:mm", "tot HH:mm", "vanaf HH:mm tot HH:mm", or "hele dag".
   */
  static formatTimeRange(start: Moment | null | undefined, end: Moment | null | undefined): string {
    const hasStart = start && !isMidnight(start)
    const hasEnd = end && !isMidnight(end)

    if (hasStart && hasEnd) {
      return `vanaf ${start.format("HH:mm")} tot ${end.format("HH:mm")} uur`
    }

    if (hasStart) {
      return `vanaf ${start.format("HH:mm")} uur`
    }

    if (hasEnd) {
      return `tot ${end.format("HH:mm")} uur`
    }

    return "hele dag"
  }

  /**
   * Returns a human-readable time range string for an availability block.
   * When a contextDate is provided, the range is adjusted to show only the portion
   * that overlaps that specific day.
   */
  static formatAvailabilityBlockTimeRange(
    availabilityBlock: AvailabilityBlock,
    contextDate?: Moment
  ): string {
    let effectiveStart = this.adjustForMidnight(availabilityBlock.timeStart, false) ?? null
    let effectiveEnd = this.adjustForMidnight(availabilityBlock.timeEnd, true) ?? null

    if (contextDate) {
      const dayStart = contextDate.clone().startOf("day")
      const dayEnd = contextDate.clone().add(1, "day").startOf("day")

      if (effectiveStart?.isBefore(dayStart)) {
        effectiveStart = dayStart
      }

      if (effectiveEnd?.isAfter(dayEnd)) {
        effectiveEnd = null
      }
    }

    const prefix =
      availabilityBlock.availabilityType === AvailabilityTypes.AVAILABLE
        ? "Beschikbaar "
        : "Niet beschikbaar "

    return prefix + this.formatTimeRange(effectiveStart, effectiveEnd)
  }

  /**
   * Returns a human-readable description for an AvailabilityBlock,
   * combining the time range with an optional description.
   */
  static formatAvailabilityBlock(block: AvailabilityBlock, contextDate: Moment): string {
    const timeText = this.formatAvailabilityBlockTimeRange(block, contextDate)
    return block.description ? `${timeText}: ${block.description}` : timeText
  }

  /**
   * Returns a human-readable description for an UnavailabilityBlock source.
   * For AvailabilityBlock sources: shows the time range (scoped to the given date), reason, and description.
   * For RecurringAvailability sources: delegates to formatRecurringAvailability.
   */
  static formatUnavailabilitySource(
    source: AvailabilityBlock | RecurringAvailability,
    date?: Moment
  ): string {
    if (source instanceof AvailabilityBlock) {
      return [
        this.formatAvailabilityBlockTimeRange(source, date),
        source.reason?.label,
        source.description
      ]
        .filter(Boolean)
        .join(" — ")
    }
    return this.formatRecurringAvailability(source)
  }

  /**
   * Returns a short human-readable string for a recurring availability slot,
   * e.g. "Elke maandag beschikbaar vanaf 08:00 uur tot 17:00 uur" or
   * "Elke maandag niet beschikbaar vanaf 12:00 uur tot 13:00 uur".
   */
  static formatRecurringAvailability(recurringAvailability: RecurringAvailability): string {
    const day = WeekdayLabels[recurringAvailability.dayOfWeek]
    const label =
      recurringAvailability.availabilityType === AvailabilityTypes.UNAVAILABLE
        ? "niet beschikbaar"
        : "beschikbaar"
    const timeRange = this.formatTimeRange(
      recurringAvailability.timeStart,
      recurringAvailability.timeEnd
    )
    return `Elke ${day.toLowerCase()} ${label} ${timeRange === "hele dag" ? "" : timeRange}`.trim()
  }

  // ============================================================================
  // UNAVAILABILITY COMPUTATION
  // ============================================================================

  /**
   * Computes unavailability blocks for an availability bucket within the given date range.
   * Availability is assumed by default; only explicit rules (recurring availability and
   * UNAVAILABLE blocks) restrict it.
   *
   * The returned blocks may overlap and each has a single source.
   */
  static computeUnavailabilityBlocks(
    bucket: AvailabilityBucket,
    rangeStart: Moment,
    rangeEnd: Moment
  ): UnavailabilityBlock[] {
    return [
      ...this.computeRecurringUnavailabilityBlocks(bucket, rangeStart, rangeEnd),
      ...this.computeBlockUnavailabilityBlocks(bucket, rangeStart, rangeEnd)
    ]
  }

  /**
   * Groups recurring availability slots by day of week.
   */
  private static groupByDay(slots: RecurringAvailability[]): Map<number, RecurringAvailability[]> {
    return slots.reduce((map, slot) => {
      const existing = map.get(slot.dayOfWeek) ?? []
      existing.push(slot)
      map.set(slot.dayOfWeek, existing)
      return map
    }, new Map<number, RecurringAvailability[]>())
  }

  /**
   * Returns the start/end minutes for a recurring slot, defaulting to 0 and 24*60.
   */
  private static slotMinuteRange(slot: RecurringAvailability): [number, number] {
    return [
      slot.timeStart ? this.toMinutes(slot.timeStart) : 0,
      slot.timeEnd ? this.toMinutes(slot.timeEnd) : 24 * 60
    ]
  }

  /**
   * Computes unavailability blocks derived from recurring (weekly) availability slots.
   *
   * Two types of recurring slots are handled:
   * - AVAILABLE slots: the inverse (gaps outside the slot) is returned as unavailability.
   *   Only applies when the bucket has at least one active AVAILABLE slot.
   * - UNAVAILABLE slots: the slot itself is returned as an unavailability block.
   *
   * If the bucket has no active recurring availability restrictions, returns an empty array.
   */
  static computeRecurringUnavailabilityBlocks(
    bucket: AvailabilityBucket,
    rangeStart: Moment,
    rangeEnd: Moment
  ): UnavailabilityBlock[] {
    if (!this.hasRecurringAvailabilityRestrictions(bucket)) {
      return []
    }

    const blocks: UnavailabilityBlock[] = []
    const availableSlots = this.getActiveRecurringSlots(bucket, AvailabilityTypes.AVAILABLE)
    const unavailableSlots = this.getActiveRecurringSlots(bucket, AvailabilityTypes.UNAVAILABLE)
    const availableSlotsByDay = this.groupByDay(availableSlots)
    const unavailableSlotsByDay = this.groupByDay(unavailableSlots)
    const hasAnyAvailableSlots = availableSlots.length > 0

    // Walk each day in the range
    const cursor = rangeStart.clone().startOf("day")
    const rangeEndDay = rangeEnd.clone().startOf("day")
    const end = rangeEndDay.isBefore(rangeEnd) ? rangeEndDay.add(1, "day") : rangeEndDay

    while (cursor.isBefore(end)) {
      const dayStart = cursor.clone().startOf("day")
      const dayEnd = cursor.clone().add(1, "day").startOf("day")
      const clampedDayStart = dayStart.isBefore(rangeStart) ? rangeStart.clone() : dayStart
      const clampedDayEnd = dayEnd.isAfter(rangeEnd) ? rangeEnd.clone() : dayEnd

      const isoWeekday = cursor.isoWeekday()

      // --- AVAILABLE slots: compute inverse (gaps) as unavailability ---
      if (hasAnyAvailableSlots) {
        for (const slot of availableSlotsByDay.get(isoWeekday) ?? []) {
          const [slotStartMin, slotEndMin] = this.slotMinuteRange(slot)

          if (slotStartMin > 0) {
            const gapEnd = clampedDayStart.clone().add(slotStartMin, "minutes")
            if (gapEnd.isAfter(clampedDayStart)) {
              blocks.push({ source: slot, timeStart: clampedDayStart.clone(), timeEnd: gapEnd })
            }
          }
          if (slotEndMin < 24 * 60) {
            const gapStart = clampedDayStart.clone().add(slotEndMin, "minutes")
            if (gapStart.isBefore(clampedDayEnd)) {
              blocks.push({ source: slot, timeStart: gapStart, timeEnd: clampedDayEnd.clone() })
            }
          }
        }
      }

      // --- UNAVAILABLE slots: the slot itself is the unavailability ---
      for (const slot of unavailableSlotsByDay.get(isoWeekday) ?? []) {
        const [slotStartMin, slotEndMin] = this.slotMinuteRange(slot)
        const effectiveStart = clampedDayStart.clone().add(slotStartMin, "minutes")
        const effectiveEnd = clampedDayStart.clone().add(slotEndMin, "minutes")

        // Clamp to the day/range boundaries
        const start = effectiveStart.isBefore(clampedDayStart)
          ? clampedDayStart.clone()
          : effectiveStart
        const end = effectiveEnd.isAfter(clampedDayEnd) ? clampedDayEnd.clone() : effectiveEnd

        if (start.isBefore(end)) {
          blocks.push({ source: slot, timeStart: start, timeEnd: end })
        }
      }

      cursor.add(1, "day")
    }

    return blocks
  }

  /**
   * Computes unavailability blocks from explicit UNAVAILABLE availability blocks.
   * Each block is clamped to the requested range and only included if it overlaps.
   * Midnight boundaries are expanded to cover the full day.
   */
  static computeBlockUnavailabilityBlocks(
    bucket: AvailabilityBucket,
    rangeStart: Moment,
    rangeEnd: Moment
  ): UnavailabilityBlock[] {
    return this.getTimeBoundedUnavailableBlocks(bucket).reduce<UnavailabilityBlock[]>(
      (blocks, block) => {
        const effectiveStart = block.timeStart
          ? this.adjustForMidnight(block.timeStart, false)!.clone()
          : rangeStart.clone()
        const effectiveEnd = block.timeEnd
          ? this.adjustForMidnight(block.timeEnd, true)!.clone()
          : rangeEnd.clone()

        if (effectiveStart.isBefore(rangeEnd) && effectiveEnd.isAfter(rangeStart)) {
          blocks.push({
            source: block,
            timeStart: effectiveStart.isBefore(rangeStart) ? rangeStart.clone() : effectiveStart,
            timeEnd: effectiveEnd.isAfter(rangeEnd) ? rangeEnd.clone() : effectiveEnd
          })
        }
        return blocks
      },
      []
    )
  }

  /**
   * Convenience function to compute unavailability blocks for a single calendar day.
   */
  static computeUnavailabilityBlocksForDate(
    bucket: AvailabilityBucket,
    date: Moment
  ): UnavailabilityBlock[] {
    const dayStart = date.clone().startOf("day")
    const dayEnd = date.clone().add(1, "day").startOf("day")
    return this.computeUnavailabilityBlocks(bucket, dayStart, dayEnd)
  }

  // ============================================================================
  // CONVENIENCE QUERIES
  // ============================================================================

  /**
   * Returns whether a bucket is available at the exact given moment.
   * Checks that the moment is within recurring availability and not covered by any unavailability block.
   */
  static isAvailableAtMoment(bucket: AvailabilityBucket, moment: Moment): boolean {
    return (
      this.isWithinRecurringAvailability(bucket, moment) &&
      this.findUnavailableBlocksForDate(bucket, moment).length === 0
    )
  }

  /**
   * Returns whether the bucket has no unavailability on the given calendar day.
   */
  static isAvailableOnDate(bucket: AvailabilityBucket, date: Moment): boolean {
    return this.computeUnavailabilityBlocksForDate(bucket, date).length === 0
  }

  /**
   * Returns whether the bucket is partially available on the given calendar day.
   */
  static isPartiallyAvailableOnDate(bucket: AvailabilityBucket, date: Moment): boolean {
    const blocks = this.computeUnavailabilityBlocksForDate(bucket, date)
    return blocks.length > 0 && !blocks.some((block) => this.coversEntireDay(block, date))
  }

  /**
   * Returns whether the bucket is unavailable for the full calendar day.
   */
  static isEntireDayUnavailableOnDate(bucket: AvailabilityBucket, date: Moment): boolean {
    const blocks = this.computeUnavailabilityBlocksForDate(bucket, date)
    return blocks.some((block) => this.coversEntireDay(block, date))
  }

  /**
   * Returns whether a single unavailability block covers the entire calendar day.
   */
  static coversEntireDay(block: UnavailabilityBlock, date: Moment): boolean {
    const dayStart = date.clone().startOf("day")
    const dayEnd = date.clone().add(1, "day").startOf("day")
    return block.timeStart.isSameOrBefore(dayStart) && block.timeEnd.isSameOrAfter(dayEnd)
  }
}
